import request from '@/src/apis/axios';
import { ListResponse, ObjectResponse } from '@/src/types/common';
import { plainToInstance } from 'class-transformer';
import {
  GetChallengeRequestParams,
  UpsertChallengeRequestBody,
  GetChallengeParticipantGroupByRequestParams,
  GetChallengeParticipantByIdRequestParams,
} from '@/src/apis/rest/challenge.dtos';
import { ChallengeParticipantDto } from '@/src/models/ChallengeParticipantDto';
import { ChallengeDto } from '@/src/models/ChallengeDto';

const URL = {
  challenges: '/admin/challenges',
  challengeDetail: '/admin/challenges/{id}',
  challengeParticipant: '/admin/challenge-participants',
  challengeParticipantDetail: '/admin/challenge-participants/{id}',
  challengeParticipantByGroup: '/admin/challenge-participants/group-by',
  challengeParticipantExcel: '/admin/challenge-participants/excel-export',
  challengesExcel: '/admin/challenges/excel/export',
};

export const getChallengesAPI = async (
  requestParams: Partial<GetChallengeRequestParams>,
) => {
  const { page = 1, pageSize = 10, ...rest } = requestParams;

  const { data } = await request<ListResponse<ChallengeDto>>({
    method: 'get',
    url: URL.challenges,
    queryParams: { ...rest, page, pageSize },
  });

  const rows = plainToInstance(ChallengeDto, data.rows);

  return { rows, count: data.count };
};

export const getChallengeByIdAPI = async (id: number) => {
  const { data } = await request<ObjectResponse<ChallengeDto>>({
    method: 'get',
    url: URL.challengeDetail.replace('{id}', String(id)),
  });

  return plainToInstance(ChallengeDto, data.row);
};

export const postChallengeAPI = async (
  requestBody: Partial<UpsertChallengeRequestBody>,
) => {
  const { data } = await request<ObjectResponse<ChallengeDto>>({
    method: 'post',
    url: URL.challenges,
    requestBody,
  });

  return plainToInstance(ChallengeDto, data.row);
};

export const putChallengeAPI = async (
  id: number,
  requestBody: Partial<UpsertChallengeRequestBody>,
) => {
  const { data } = await request<ObjectResponse<ChallengeDto>>({
    method: 'put',
    url: URL.challengeDetail.replace('{id}', String(id)),
    requestBody,
  });

  return plainToInstance(ChallengeDto, data.row);
};

export const deleteChallengeAPI = async (id: number) => {
  const { data } = await request({
    method: 'delete',
    url: URL.challengeDetail.replace('{id}', String(id)),
  });

  return data;
};

export const getChallengeParticipantByIdAPI = async (
  requestParams: GetChallengeParticipantByIdRequestParams,
) => {
  const { page = 1, pageSize = 10, ...rest } = requestParams;
  const { data } = await request<ListResponse<ChallengeParticipantDto>>({
    method: 'get',
    url: URL.challengeParticipant,
    queryParams: { ...rest, page, pageSize },
  });

  const rows = plainToInstance(ChallengeParticipantDto, data.rows);

  return { rows, count: data.count };
};

export const getChallengeParticipantByGroupAPI = async (
  requestParams: Partial<GetChallengeParticipantGroupByRequestParams>,
) => {
  const { page = 1, pageSize = 10, ...rest } = requestParams;

  const { data } = await request<ListResponse<ChallengeParticipantDto>>({
    method: 'get',
    url: URL.challengeParticipantByGroup,
    queryParams: { ...rest, page, pageSize },
  });

  const rows = plainToInstance(ChallengeParticipantDto, data.rows);
  return { rows, count: data.count };
};

export const getChallengeParticipantExcelAPI = async (
  password: string,
  requestParams: Partial<GetChallengeParticipantGroupByRequestParams>,
) => {
  const { data } = await request<Blob>({
    method: 'get',
    url: URL.challengeParticipantExcel,
    queryParams: { ...requestParams, password },
    config: { responseType: 'blob' },
  });

  return data;
};

export const putChallengeParticipantAPI = async (id: number) => {
  const { data } = await request<ObjectResponse<ChallengeDto>>({
    method: 'put',
    url: URL.challengeParticipantDetail.replace('{id}', String(id)),
  });

  return plainToInstance(ChallengeDto, data.row);
};

export const getChallengesExcelAPI = async (
  password: string,
  requestParams: Partial<GetChallengeRequestParams>,
) => {
  const { data } = await request<Blob>({
    method: 'get',
    url: URL.challengesExcel,
    queryParams: { password, ...requestParams },
    config: { responseType: 'blob' },
  });

  return data;
};
